import throttle from 'lodash/throttle';
import {gsap} from 'gsap';

export class WbTabArrows {
  constructor(tablist) {
    this.tablist = tablist;
    this.init();
  }

  init() {
    this.bindListeners();
    this.resize();
  }

  bindListeners() {
    const that = this;
    const nextArrow = this.tablist.parentNode.querySelector('.wb-tab-arrow--next');
    const prevArrow = this.tablist.parentNode.querySelector('.wb-tab-arrow--prev');

    nextArrow.addEventListener('click', that.next.bind(that));
    prevArrow.addEventListener('click', that.prev.bind(that));
    window.addEventListener('resize', throttle(that.resize.bind(that), 300));
    document.addEventListener('_page_loaded', that.resize.bind(that));

    const details = this.tablist.closest('details');
    if (details) details.querySelector('summary').addEventListener('click', that.resize.bind(that));
  }

  checkOverflow() {
    return this.tablist.offsetWidth < this.tablist.scrollWidth;
  }

  resize(event) {
    const that = this;
    const parent = that.tablist.parentNode;

    // Prevent event bubbling
    if (event && event.target !== event.currentTarget) return;

    if (that.resizeTimeout) clearTimeout(that.resizeTimeout);

    that.resizeTimeout = setTimeout(() => {
      if (that.tablist) {
        gsap.to(that.tablist.tabs, { x: 0, duration: 0 });

        if (that.checkOverflow()) parent.classList.add('show-arrows');
        else parent.classList.remove('show-arrows');

        that.setPosition();
      }
    }, 150);
  }

  next() {
    const that = this;
    const parent = this.tablist.parentNode;

    if (parent.classList.contains('is-animating')) return;

    const { tabs } = this.tablist;
    const gap = tabs[1].offsetLeft - (tabs[0].offsetLeft + tabs[0].offsetWidth);
    const distance = tabs[this.offsetIndex].offsetWidth + gap;

    this.offsetIndex += 1;

    parent.classList.add('is-animating');

    gsap.to(tabs, {
      x: `-=${distance}`,
      duration: 0.35,
      onComplete() {
        that.toggleDisableArrows();
      },
    });
  }

  prev() {
    const that = this;
    const parent = this.tablist.parentNode;

    if (parent.classList.contains('is-animating')) return;

    const { tabs } = this.tablist;
    const gap = tabs[1].offsetLeft - (tabs[0].offsetLeft + tabs[0].offsetWidth);
    const distance = tabs[this.offsetIndex - 1].offsetWidth + gap;

    this.offsetIndex -= 1;

    parent.classList.add('is-animating');

    gsap.to(tabs, {
      x: `+=${distance}`,
      duration: 0.35,
      onComplete() {
        that.toggleDisableArrows();
      },
    });
  }

  toggleDisableArrows() {
    const parent = this.tablist.parentNode;

    if (this.offsetIndex === 0) {
      parent.querySelector('.wb-tab-arrow--prev').setAttribute('disabled', 'disabled');
    } else {
      parent.querySelector('.wb-tab-arrow--prev').removeAttribute('disabled');
    }

    if (!this.checkOverflow()) {
      parent.querySelector('.wb-tab-arrow--next').setAttribute('disabled', 'disabled');
    } else {
      parent.querySelector('.wb-tab-arrow--next').removeAttribute('disabled');
    }

    parent.classList.remove('is-animating');
  }

  setPosition() {
    const that = this;
    const { tabs } = this.tablist;
    const { index } = this.tablist.querySelector('[aria-selected="true"]');
    let distance = 0;

    this.offsetIndex = 0;

    if (this.checkOverflow() && index !== 0) {
      const gap = tabs[1].offsetLeft - (tabs[0].offsetLeft + tabs[0].offsetWidth);
      const arrowWidth = this.tablist.parentNode.querySelector('.wb-tab-arrow--next').offsetWidth * 2 + 5;
      const currentTabWidth = tabs[index].offsetWidth;

      if (currentTabWidth > this.tablist.offsetWidth - arrowWidth) {
        distance = tabs[index].offsetLeft;
        this.offsetIndex = index - 1;
      } else {
        let prevTabsWidth = 0;

        for (let i = index - 1; i >= 0; i -= 1) {
          prevTabsWidth += tabs[i].offsetWidth + gap;
          const fitsInTablist = prevTabsWidth + currentTabWidth < this.tablist.offsetWidth - arrowWidth;

          distance = tabs[i].offsetLeft;
          this.offsetIndex = i;

          if (!fitsInTablist) {
            distance = tabs[i + 1].offsetLeft;
            this.offsetIndex = i + 1;
            break;
          }
        }
      }
    }

    gsap.to(tabs, {
      x: -distance,
      duration: 0,
      onComplete: () => that.toggleDisableArrows(),
    });
  }
}
